import React from 'react'
import { cn } from '@/utils'
import { FileText, BarChart3, DollarSign, Users, Unlock, TrendingUp, Calendar } from 'lucide-react'

export interface FundingMetricCardProps {
  icon: 'projects' | 'rounds' | 'amount' | 'investors' | 'unlock' | 'chart' | 'calendar'
  label: string
  value: string
  percentChange?: {
    value: string
    direction: 'up' | 'down'
    color: 'green' | 'red'
  }
  chart?: React.ReactNode
  className?: string
}

const iconMap = {
  projects: FileText,
  rounds: BarChart3,
  amount: DollarSign,
  investors: Users,
  unlock: Unlock,
  chart: TrendingUp,
  calendar: Calendar,
}

export const FundingMetricCard: React.FC<FundingMetricCardProps> = ({
  icon,
  label,
  value,
  percentChange,
  chart,
  className,
}) => {
  const Icon = iconMap[icon]

  return (
    <div
      className={cn(
        'relative bg-white dark:bg-[#121212] h-[100px] overflow-hidden',
        'border-0 rounded-none',
        className
      )}
    >
      {/* Main content */}
      <div className="absolute flex flex-col h-[76px] items-start justify-between left-[24px] top-[12px]">
        {/* Header with icon and label */}
        <div className="flex gap-[6px] items-center w-full">
          <div className="shrink-0 size-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            <Icon className="w-full h-full" strokeWidth={1.5} />
          </div>
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[18px] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            {label}
          </p>
        </div>

        {/* Value and percentage change */}
        <div className="flex gap-[4px] items-center w-full">
          <p className="font-['Plus_Jakarta_Sans'] font-medium leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] text-[23px] tracking-[-0.2px]">
            {value}
          </p>
          {percentChange && (
            <div
              className={cn(
                'bg-[rgba(242,241,246,0.64)] dark:bg-[rgba(45,41,35,0.24)]',
                'flex gap-[4px] items-center justify-center',
                'pb-[4px] pl-[6px] pr-[8px] pt-[3px]',
                'rounded-[16px]',
                'shadow-[inset_1px_1px_4px_0px_rgba(217,214,227,0.4)] dark:shadow-[inset_1px_1px_4px_0px_rgba(0,0,0,0.4)]'
              )}
            >
              <div className="shrink-0 size-[12px]">
                {percentChange.direction === 'up' ? (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 8L3 2M3 2H8M3 2V7"
                      stroke={percentChange.color === 'green' ? '#2ea66b' : '#ff540c'}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 4L3 10M3 10H8M3 10V5"
                      stroke={percentChange.color === 'green' ? '#2ea66b' : '#ff540c'}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <p
                className={cn(
                  'font-["Plus_Jakarta_Sans"] font-semibold leading-[16px] text-[12px]',
                  percentChange.color === 'green' ? 'text-[#2ea66b]' : 'text-[#ff540c]'
                )}
              >
                {percentChange.value}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Chart in the background */}
      {chart && (
        <div className="absolute h-[100px] right-0 top-0 w-[185px] overflow-hidden">
          {chart}
        </div>
      )}
    </div>
  )
}

export default FundingMetricCard
